import { SetStateAction, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { RepeatableQcFieldName } from './const';
import { UseDataProps } from './types';
import { flatten } from '80.quickConnect.Core/helpers';
import { AllFieldValueTypes, DeclarationContext, FieldDesc, RepeatableGroupDesc } from '90.quickConnect.Models/models';
import updateItemsFieldOrChild from '20.formLib/helpers/updateField/updateItemsFieldOrChild';
import { useStore } from '30.quickConnect.Stores';
import { isFocusableField } from '80.quickConnect.Core/helpers/isFocusableField';
import useFormEventQCScript from '10.quickConnect.app/components/domain/Declaration/hooks/useFormEventQCScript';
import { useQCSFunctions } from '20.formLib/DeclarationContainer/contexts';
import {
  appendRowsToRepeatableGroup,
  removeRowAtFromRepeatableGroup,
} from '20.formLib/helpers/repeatableGroup/mutations';

const useData = (
  field: RepeatableGroupDesc,
  updateDeclaration: (updatedFieldFullPathId: string, newValue: AllFieldValueTypes) => void,
  setDeclaration: (value: SetStateAction<FieldDesc[] | undefined>) => void,
  context: DeclarationContext,
  setSelectedIndex: React.Dispatch<React.SetStateAction<string | undefined>>,
  flattenFields: FieldDesc[],
  openByImagesGroupField?: boolean,
): UseDataProps => {
  const { t } = useTranslation('declaration');
  const { id, fullPathId, items, value, maxRow, desactivatedAutomation } = field;

  const nameWithId = `${RepeatableQcFieldName}-${id}`;

  const [isExpanded, setIsExpanded] = useState(value ? (value as boolean) : openByImagesGroupField ?? false);
  const [isAllExpanded, setIsAllExpanded] = useState(value ? (value as boolean) : openByImagesGroupField ?? false);

  const qcsFormEvent = useFormEventQCScript();
  const qcsFunctions = useQCSFunctions();

  const {
    DeclarationStore: { setFieldFocused, setCurrentBaseScope },
  } = useStore();

  useEffect(() => {
    if (!isExpanded) {
      setSelectedIndex(undefined);
    }
  }, [isExpanded, setSelectedIndex]);
  const {
    QCScriptStore: { setQCSBaseFieldId, setQCSFieldId, setFlattenFields },
    DeclarationStore: { requiredField, verifRequiredField },
  } = useStore();

  const handleChange = useCallback(
    (expanded: boolean) => {
      setIsExpanded(!expanded);
      setIsAllExpanded((prev) => (prev = !expanded));
      updateDeclaration(fullPathId, !expanded);
    },
    [fullPathId, updateDeclaration],
  );

  // Methode de mise a jour des Form_event ON_ROW_ADDED et On_ROW_DELETED
  const formEventOnRow = useCallback(
    (onRowAdded: boolean, currentFlattenFields: FieldDesc[]) => {
      // Dans le cas des qcscript, on appelle et que le champ desactivatedAutomation est mis a false
      if (qcsFormEvent.isQCScriptForm && !desactivatedAutomation) {
        setQCSBaseFieldId(fullPathId);
        setQCSFieldId(id);
        setFlattenFields(currentFlattenFields);
        qcsFormEvent.setQCSFunc(qcsFunctions);
        if (onRowAdded) qcsFormEvent.onRowAdded(currentFlattenFields, fullPathId);
        else qcsFormEvent.onRowDeleted(currentFlattenFields, fullPathId);
      }
    },
    [
      qcsFormEvent,
      desactivatedAutomation,
      setQCSBaseFieldId,
      fullPathId,
      setQCSFieldId,
      id,
      setFlattenFields,
      qcsFunctions,
    ],
  );

  const addItem = useCallback(
    (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
      // Evite de déplier l'accordéon
      if (isExpanded) event.stopPropagation();
      const { nextItems: newItems, addedRows } = appendRowsToRepeatableGroup(field, context, 1);

      if (addedRows.length > 0) {
        setDeclaration((prev) => {
          if (prev && prev.length > 0) {
            const normalizedFullPathId = fullPathId.toLowerCase();
            prev.forEach((root) => updateItemsFieldOrChild(normalizedFullPathId, root, newItems));
            return [...prev];
          }
        });

        const [newIncludeField] = addedRows;
        const newIncludeChildren = newIncludeField.items;

        // Donner le focus au premier champ du groupe
        setFieldFocused(newIncludeChildren.find(isFocusableField)?.fullPathId);

        // Stocker le current scope
        setCurrentBaseScope(newIncludeField.fullPathId);

        // Dans le cas des qcscript, on appelle onRowAdded avec tous les descendants aplatis
        const allNewFields = flatten([newIncludeField], (f) => f.items);
        formEventOnRow(true, [...flattenFields, ...allNewFields]);
      } else {
        toast.warn(t('fields_repeatable_group_warnings_max', { maxRow }).toString());
      }
    },
    [
      field,
      fullPathId,
      isExpanded,
      flattenFields,
      maxRow,
      setDeclaration,
      t,
      setFieldFocused,
      setCurrentBaseScope,
      context,
      formEventOnRow,
    ],
  );

  const removeItem = useCallback(
    (event: React.MouseEvent<HTMLButtonElement, MouseEvent>, itemToDelete: string) => {
      // Evite de déplier l'accordéon
      event.stopPropagation();
      const index = items.findIndex((f) => f.fullPathId === itemToDelete);
      if (index > -1) {
        const { nextItems: newItemsWithNewIndex, removed } = removeRowAtFromRepeatableGroup(field, index);
        if (!removed) return;

        setDeclaration((prev) => {
          if (prev && prev.length > 0) {
            const normalizedFullPathId = fullPathId.toLowerCase();
            prev.forEach((root) => updateItemsFieldOrChild(normalizedFullPathId, root, newItemsWithNewIndex));
            return [...prev];
          }
        });

        // on retire le focus
        setFieldFocused(undefined);

        // Dans le cas des qcscript, on appelle onRowDeleted avec tous les descendants aplatis
        const allRemainingFields = flatten(newItemsWithNewIndex, (f) => f.items);
        formEventOnRow(false, [...flattenFields, ...allRemainingFields]);
      }
    },
    [items, setDeclaration, setFieldFocused, formEventOnRow, flattenFields, fullPathId, field],
  );

  const expandOrFoldAll = useCallback((event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    // Evite de déplier l'accordéon
    event.stopPropagation();
    setIsAllExpanded((prev) => !prev);
  }, []);

  useEffect(() => {
    if (verifRequiredField) {
      if (requiredField && requiredField?.fullPathId.includes(fullPathId)) {
        handleChange(!verifRequiredField);
        //  setVerifRequiredField(false);
      }
    }
  }, [fullPathId, handleChange, requiredField, verifRequiredField]);

  return {
    t,
    nameWithId,
    isExpanded,
    handleChange,
    items,
    addItem,
    removeItem,
    isAllExpanded,
    expandOrFoldAll,
  };
};

export default useData;
